import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'

import { Locale, LocaleSearchParams } from './locale.model'
import { Page } from '../../models/util.model'
import { LOCALES_LIST } from '../../consts/locale.const'

@Injectable({
  providedIn: 'root',
})
export class LocalesService {
  /**
   * Search locales by params
   * @param params - The search params
   * @param returnAll - the returnAll flag
   * @returns The observable<Page<Locale>> for search locales
   */
  search$(params?: LocaleSearchParams): Observable<Page<Locale>> {
    const sortedLocales = this.sortLocales(LOCALES_LIST)
    const filteredLocales = this.applyFilters(sortedLocales, params)

    const page: Page<Locale> = {
      data: filteredLocales,
      totalCount: LOCALES_LIST.length,
    }

    return of(page)
  }

  /**
   * List locales by params
   * @param params - The search params
   * @param returnAll - the returnAll flag
   * @returns The observable<Locale[]> for list locales
   */
  list$(params?: LocaleSearchParams): Observable<Locale[]> {
    const sortedLocales = this.sortLocales(LOCALES_LIST)
    const filteredLocales = this.applyFilters(sortedLocales, params)
    return of(filteredLocales)
  }

  /**
   * Read a locale by ID
   * @param localeId - the locale ID
   * @returns the observable for read the locale
   */
  read$(tag: string): Observable<Locale | undefined> {
    const locale = LOCALES_LIST.filter((l) => l.tag === tag)
    return of(locale ? locale[0] : undefined)
  }

  /**
   * Find a locale by params
   * @param params - The search params
   * @returns the observable<Locale> for find a locale
   */
  readOne$(params?: LocaleSearchParams): Observable<Locale | undefined> {
    const filteredLocales = this.applyFilters(undefined, params)
    return of(filteredLocales.length ? filteredLocales[0] : undefined)
  }

  /**
   * Filters the LOCALES_LIST based on the provided search parameters
   * @param searchParams - The search parameters to filter by
   * @returns Filtered array of locales
   */
  private applyFilters(
    list: Locale[] | undefined,
    searchParams?: LocaleSearchParams,
  ): Locale[] {
    if (!searchParams) {
      return LOCALES_LIST
    }

    let filteredLocales = [...(list ?? LOCALES_LIST)]

    // Filter by language
    if (searchParams?.language) {
      filteredLocales = filteredLocales.filter(
        (locale) =>
          locale.language?.toLowerCase() ===
          searchParams.language?.toLowerCase(),
      )
    }

    // Filter by language contains
    if (searchParams['language:ct']) {
      filteredLocales = filteredLocales.filter((locale) =>
        locale.language
          ?.toLowerCase()
          .includes(searchParams['language:ct']?.toLowerCase() || ''),
      )
    }

    // Filter by name
    if (searchParams.name) {
      filteredLocales = filteredLocales.filter(
        (locale) =>
          locale.name?.toLowerCase() === searchParams.name?.toLowerCase(),
      )
    }

    // Filter by name contains
    if (searchParams['name:ct']) {
      filteredLocales = filteredLocales.filter((locale) =>
        locale.name
          ?.toLowerCase()
          .includes(searchParams['name:ct']?.toLowerCase() || ''),
      )
    }

    // Filter by tag
    if (searchParams.tag) {
      if (Array.isArray(searchParams.tag)) {
        filteredLocales = filteredLocales.filter((locale) =>
          searchParams.tag?.includes(locale.tag),
        )
      } else {
        filteredLocales = filteredLocales.filter(
          (locale) =>
            locale.tag.toLowerCase() === `${searchParams.tag}`.toLowerCase(),
        )
      }
    }

    // Filter by tag contains
    if (searchParams['tag:ct']) {
      filteredLocales = filteredLocales.filter((locale) =>
        locale.tag
          .toLowerCase()
          .includes(searchParams['tag:ct']?.toLowerCase() || ''),
      )
    }

    // Apply sorting if specified
    if (searchParams.sort) {
      const sortField = searchParams.sort
      const order = searchParams.order || 'asc'

      filteredLocales.sort((a, b) => {
        const aValue = a[sortField] || ''
        const bValue = b[sortField] || ''

        if (order === 'asc') {
          return aValue.localeCompare(bValue)
        } else {
          return bValue.localeCompare(aValue)
        }
      })
    }

    // Apply limit and offset
    if (searchParams.offset) {
      filteredLocales = filteredLocales.slice(searchParams.offset)
    }

    if (searchParams.limit) {
      filteredLocales = filteredLocales.slice(0, searchParams.limit)
    }

    return filteredLocales
  }

  /**
   * Sorts a list of locales alphabetically by language first, then by location
   * @param locales - Array of locales to sort
   * @returns Sorted array of locales
   */
  private sortLocales(locales: Locale[]): Locale[] {
    return [...locales].sort((a, b) => {
      // First, compare by language
      const languageA = a.language || ''
      const languageB = b.language || ''
      const languageComparison = languageA.localeCompare(languageB)

      // If languages are different, return the language comparison
      if (languageComparison !== 0) {
        return languageComparison
      }

      // If languages are the same, compare by location
      const locationA = a.location || ''
      const locationB = b.location || ''
      return locationA.localeCompare(locationB)
    })
  }
}
